import { FormControlLabel, Radio as MuiRadio, RadioGroup as MuiRadioGroup } from '@mui/material';
import React, { forwardRef } from 'react';
import { CommonDataProps, SxProps } from '../../utils';
import { Typography } from '../Typography';

export interface RadioLabelItem {
  label: string | React.ReactNode;
  value: string;
  description?: string;
  disabled?: boolean;
}

export type RadioLabelProps = Omit<RadioLabelItem, 'value'>;

export interface RadioGroupProps extends CommonDataProps {
  /**
   * Callback fired when a radio button is selected.
   */
  onChange: (event: React.ChangeEvent<HTMLInputElement>, value: string) => void;
  /**
   * Value of the selected radio button.
   */
  value: string;
  /**
   * List of values
   */
  values: Array<string | RadioLabelItem>;
  /**
   * Display group of elements in a compact row or column.
   * @default column
   */
  direction?: 'column' | 'row';
  /**
   * If true, the component is disabled.
   * @default false
   */
  disabled?: boolean;
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps;
}

export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(function RadioGroup(
  { values, disabled, direction, ...props },
  ref
) {
  const isRow = direction === 'row';

  const renderRadioLabel = (radioLabel: string | RadioLabelItem, index: number) => {
    let label: string | React.ReactNode = '';
    let description: string | undefined;
    let itemDisabled = false;
    let value;

    if (typeof radioLabel === 'string') {
      label = radioLabel;
      value = radioLabel;
    } else {
      value = radioLabel.value;
      label = radioLabel.label;
      description = isRow ? undefined : radioLabel.description; // horizontally arranged radio groups should not include descriptions
      itemDisabled = !!radioLabel.disabled;
    }

    return (
      <FormControlLabel
        key={index}
        value={value}
        control={<MuiRadio />}
        disabled={itemDisabled || disabled}
        label={<RadioLabel label={label} description={description} disabled={itemDisabled || disabled} />}
        sx={(theme) => ({
          alignItems: 'flex-start',
          marginY: theme.spacing(1),
        })}
      />
    );
  };

  return (
    <MuiRadioGroup ref={ref} {...props} row={isRow}>
      {values.map(renderRadioLabel)}
    </MuiRadioGroup>
  );
});

const RadioLabel = ({ label, description, disabled }: RadioLabelProps) => {
  return (
    <div>
      {/* align label with the radio  */}
      <Typography
        variant="body1"
        color={disabled ? 'text.disabled' : 'text.primary'}
        sx={{ height: '28px', position: 'relative', top: '6px' }}
      >
        {label}
      </Typography>
      {description && (
        <Typography variant="body2" color={disabled ? 'text.disabled' : 'text.legacySecondary'}>
          {description}
        </Typography>
      )}
    </div>
  );
};
